import React, { useEffect, useState, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { usePageHeader } from '../components/Layout';
import { Ic } from '../design/icons';
import { Avatar, EmptyState } from '../design/primitives';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Projects() {
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, isWorkspaceAdmin } = useWorkspace();
  const [projects, setProjects] = useState([]);
  const [, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [deleteMode, setDeleteMode] = useState('cascade'); // 'cascade' | 'transfer'
  const [transferTarget, setTransferTarget] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });

  // In a workspace context, project create/update/delete requires workspace admin
  // (matches backend require_workspace_admin). Without workspace, fall back to legacy perms.
  const canManage = activeWorkspaceId
    ? isWorkspaceAdmin
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_manage_projects')
      );

  useEffect(() => {
    Promise.all([fetchProjects(), fetchTeams(), fetchUsers(), fetchTasks()])
      .finally(() => setLoading(false));
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      const sorted = [...res.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      setProjects(sorted);
    } catch (error) {
      toast.error('Projeler yüklenemedi');
    }
  };

  const fetchTeams = async () => {
    try {
      const res = await axios.get(`${API}/teams`);
      setTeams(res.data);
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch {}
  };

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/tasks`);
      setTasks(res.data);
    } catch {}
  };

  const resetForm = () => setFormData({ name: '', description: '' });

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/projects`, formData);
      toast.success('Proje oluşturuldu');
      setShowCreate(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      // Quota errors are handled by the global UpgradeModal listener.
      if (error.response?.status === 402 && error.response?.data?.detail?.error === 'quota_exceeded') return;
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (detail?.message || 'Bilinmeyen hata');
      toast.error('Proje oluşturulamadı: ' + message);
    }
  };

  const openEdit = (project) => {
    setProjectToEdit(project);
    setFormData({ name: project.name, description: project.description || '' });
    setShowEdit(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!canManage) {
      toast.error('Bu projeyi düzenleme yetkiniz yok');
      setShowEdit(false);
      return;
    }
    try {
      await axios.patch(`${API}/projects/${projectToEdit.id}`, formData);
      toast.success('Proje güncellendi');
      setShowEdit(false);
      setProjectToEdit(null);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const openDeleteModal = async (project) => {
    setProjectToDelete(project);
    setShowDelete(true);
    setDeleteMode('cascade');
    setTransferTarget('');
    setDeleteImpact(null);
    setDeleteImpactLoading(true);
    try {
      const res = await axios.get(`${API}/projects/${project.id}/delete-impact`);
      setDeleteImpact(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Etki bilgisi alınamadı');
    } finally {
      setDeleteImpactLoading(false);
    }
  };

  const closeDeleteModal = () => {
    if (deleteSubmitting) return;
    setShowDelete(false);
    setProjectToDelete(null);
    setDeleteImpact(null);
    setDeleteMode('cascade');
    setTransferTarget('');
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    if (!canManage) {
      toast.error('Bu projeyi silme yetkiniz yok');
      setShowDelete(false);
      return;
    }
    if (deleteMode === 'transfer' && !transferTarget) {
      toast.error('Görevlerin taşınacağı hedef projeyi seçin');
      return;
    }
    setDeleteSubmitting(true);
    try {
      const params = { mode: deleteMode };
      if (deleteMode === 'transfer') params.targetProjectId = transferTarget;
      const res = await axios.delete(`${API}/projects/${projectToDelete.id}`, { params });
      const affected = res.data?.tasksAffected ?? 0;
      if (deleteMode === 'transfer') {
        toast.success(`Proje silindi. ${affected} görev hedef projeye taşındı.`);
      } else if (affected > 0) {
        toast.success(`Proje ve ${affected} görev kalıcı olarak silindi.`);
      } else {
        toast.success('Proje silindi');
      }
      closeDeleteModal();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const getUser = (userId) => users.find(u => u.id === userId) || null;

  const taskCountByProject = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      if (!t.projectId) return;
      map[t.projectId] = map[t.projectId] || { total: 0, active: 0, completed: 0, overdue: 0 };
      map[t.projectId].total += 1;
      if (t.status === 'tamamlandı' || t.status === 'tamamlandi' || t.status === 'completed') {
        map[t.projectId].completed += 1;
      } else {
        map[t.projectId].active += 1;
        if (t.dueDate && new Date(t.dueDate) < new Date()) {
          map[t.projectId].overdue += 1;
        }
      }
    });
    return map;
  }, [tasks]);

  const filteredProjects = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.description || '').toLowerCase().includes(q)
    );
  }, [projects, query]);

  const actions = (
    <div className="flex items-center gap-2">
      <div className="relative" style={{ minWidth: 220 }}>
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 ox-text-dim">{Ic.search}</span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Proje ara..."
          className="ox-input pl-8"
        />
      </div>
      {canManage && (
        <button className="ox-btn ox-btn-primary" onClick={() => { resetForm(); setShowCreate(true); }}>
          {Ic.plus} Yeni Proje
        </button>
      )}
    </div>
  );

  usePageHeader([{ label: 'Projeler' }], actions, [query, canManage]);

  return (
    <div className="ox-page space-y-4">
      {/* Summary Strip */}
      <div className="ox-surface rounded-md p-4 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
          <div>
            <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">Toplam</div>
            <div className="ox-tnum text-2xl font-semibold">{projects.length}</div>
          </div>
          <div>
            <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">Aktif Görev</div>
            <div className="ox-tnum text-2xl font-semibold" style={{ color: 'var(--accent)' }}>
              {Object.values(taskCountByProject).reduce((s, x) => s + x.active, 0)}
            </div>
          </div>
          <div>
            <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">Tamamlanan</div>
            <div className="ox-tnum text-2xl font-semibold" style={{ color: 'var(--good)' }}>
              {Object.values(taskCountByProject).reduce((s, x) => s + x.completed, 0)}
            </div>
          </div>
          <div>
            <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">Geciken</div>
            <div className="ox-tnum text-2xl font-semibold" style={{ color: 'var(--danger)' }}>
              {Object.values(taskCountByProject).reduce((s, x) => s + x.overdue, 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="ox-surface rounded-md p-12 text-center ox-text-muted">Yükleniyor...</div>
      ) : filteredProjects.length === 0 ? (
        <div className="ox-surface rounded-md">
          <EmptyState
            icon={<span style={{ fontSize: 28 }}>{Ic.projects}</span>}
            title={query ? 'Eşleşen proje bulunamadı' : 'Henüz proje yok'}
            description={query ? 'Farklı bir arama kriteri deneyin veya filtreleri sıfırlayın.' : 'Çalışmaya başlamak için ilk projenizi oluşturun. Görevler, takımlar ve dosyalar bu projeye bağlanır.'}
            action={canManage && !query && (
              <button className="ox-btn ox-btn-primary" onClick={() => { resetForm(); setShowCreate(true); }}>
                {Ic.plus} İlk Projeyi Oluştur
              </button>
            )}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filteredProjects.map((project) => {
            const counts = taskCountByProject[project.id] || { total: 0, active: 0, completed: 0, overdue: 0 };
            const managerList = (project.managerIds || []).map(id => getUser(id)).filter(Boolean);
            const teamCount = project.teamIds?.length || 0;
            const progress = counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : 0;

            return (
              <div key={project.id} className="ox-surface ox-card-hover rounded-md flex flex-col">
                <Link to={`/projects/${project.id}`} className="p-4 flex-1 flex flex-col gap-3"
                  style={{ textDecoration: 'none', color: 'inherit' }}>
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[14.5px] truncate" title={project.name}>
                        {project.name}
                      </div>
                      {project.description && (
                        <div className="text-[12.5px] ox-text-muted mt-0.5 line-clamp-2">{project.description}</div>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex gap-0.5 flex-shrink-0">
                        <button
                          className="ox-btn ox-btn-ghost p-1.5"
                          onClick={(e) => { e.preventDefault(); openEdit(project); }}
                          title="Düzenle"
                        >{Ic.edit}</button>
                        <button
                          className="ox-btn ox-btn-ghost p-1.5"
                          onClick={(e) => { e.preventDefault(); openDeleteModal(project); }}
                          title="Sil"
                          style={{ color: 'var(--danger)' }}
                        >{Ic.trash}</button>
                      </div>
                    )}
                  </div>

                  {/* Stats Row */}
                  <div className="grid grid-cols-4 gap-2 py-2" style={{ borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                    <div>
                      <div className="text-[10.5px] ox-text-muted uppercase">Toplam</div>
                      <div className="ox-tnum text-[15px] font-semibold">{counts.total}</div>
                    </div>
                    <div>
                      <div className="text-[10.5px] ox-text-muted uppercase">Aktif</div>
                      <div className="ox-tnum text-[15px] font-semibold" style={{ color: 'var(--accent)' }}>{counts.active}</div>
                    </div>
                    <div>
                      <div className="text-[10.5px] ox-text-muted uppercase">Biten</div>
                      <div className="ox-tnum text-[15px] font-semibold" style={{ color: 'var(--good)' }}>{counts.completed}</div>
                    </div>
                    <div>
                      <div className="text-[10.5px] ox-text-muted uppercase">Gecik.</div>
                      <div className="ox-tnum text-[15px] font-semibold" style={{ color: counts.overdue > 0 ? 'var(--danger)' : 'var(--text-dim)' }}>
                        {counts.overdue}
                      </div>
                    </div>
                  </div>

                  {/* Progress */}
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-[11px] ox-text-muted">İlerleme</div>
                      <div className="ox-tnum text-[11px] font-medium">{progress}%</div>
                    </div>
                    <div className="ox-track">
                      <div style={{ width: `${progress}%`, height: '100%', background: 'var(--accent)', opacity: 0.85 }} />
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between gap-2 mt-auto pt-1">
                    <div className="flex items-center gap-2 min-w-0">
                      {managerList.length > 0 ? (
                        <>
                          <div className="flex -space-x-1.5">
                            {managerList.slice(0, 3).map(m => (
                              <div key={m.id} style={{ border: '2px solid var(--surface)', borderRadius: 999 }}>
                                <Avatar u={m} imgSrc={m.picture} size={22} />
                              </div>
                            ))}
                          </div>
                          {managerList.length > 3 && (
                            <span className="text-[11px] ox-text-muted">+{managerList.length - 3}</span>
                          )}
                        </>
                      ) : (
                        <span className="text-[11.5px] ox-text-muted">Yönetici yok</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-[11.5px] ox-text-muted">
                      {Ic.teams}
                      <span className="ox-tnum">{teamCount}</span>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="ox-modal-backdrop" onClick={() => setShowCreate(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Yeni Proje</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setShowCreate(false)}>{Ic.x}</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="ox-modal-body space-y-3">
                <div>
                  <label className="text-[12px] font-medium mb-1 block">Proje Adı <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input
                    className="ox-input w-full"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: Sosyal Medya Kampanyası 2026"
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-[12px] font-medium mb-1 block">Açıklama</label>
                  <textarea
                    className="ox-input w-full"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Projenin amacını kısaca açıklayın..."
                    rows={4}
                    style={{ resize: 'vertical', minHeight: 80 }}
                  />
                </div>
              </div>
              <div className="ox-modal-foot">
                <button type="button" className="ox-btn ox-btn-ghost" onClick={() => setShowCreate(false)}>İptal</button>
                <button type="submit" className="ox-btn ox-btn-primary">Oluştur</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && (
        <div className="ox-modal-backdrop" onClick={() => setShowEdit(false)}>
          <div className="ox-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]">Projeyi Düzenle</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={() => setShowEdit(false)}>{Ic.x}</button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="ox-modal-body space-y-3">
                <div>
                  <label className="text-[12px] font-medium mb-1 block">Proje Adı <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input
                    className="ox-input w-full"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-[12px] font-medium mb-1 block">Açıklama</label>
                  <textarea
                    className="ox-input w-full"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    style={{ resize: 'vertical', minHeight: 80 }}
                  />
                </div>
              </div>
              <div className="ox-modal-foot">
                <button type="button" className="ox-btn ox-btn-ghost" onClick={() => setShowEdit(false)}>İptal</button>
                <button type="submit" className="ox-btn ox-btn-primary">Kaydet</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDelete && (
        <div className="ox-modal-backdrop" onClick={closeDeleteModal}>
          <div className="ox-modal ox-modal-sm" onClick={(e) => e.stopPropagation()} data-testid="project-delete-modal">
            <div className="ox-modal-head">
              <div className="font-semibold text-[14px]" style={{ color: 'var(--danger)' }}>Projeyi Sil</div>
              <button className="ox-btn ox-btn-ghost p-1" onClick={closeDeleteModal} disabled={deleteSubmitting}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body">
              <p className="text-[13px] mb-2">
                <strong>{projectToDelete?.name}</strong> projesini silmek istiyorsunuz.
              </p>

              {deleteImpactLoading ? (
                <p className="text-[12px] ox-text-muted">Etki bilgisi yükleniyor…</p>
              ) : deleteImpact ? (
                <>
                  <div
                    className="text-[12px]"
                    style={{
                      background: 'var(--surface-muted)',
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: 10,
                      marginBottom: 12,
                      color: 'var(--text)',
                      lineHeight: 1.6,
                    }}
                  >
                    Bu projede{' '}
                    <strong>{deleteImpact.impact?.tasks ?? 0}</strong> görev,{' '}
                    <strong>{deleteImpact.impact?.files ?? 0}</strong> dosya ve{' '}
                    <strong>{deleteImpact.impact?.comments ?? 0}</strong> yorum bulunuyor.
                  </div>

                  <div className="text-[12px] mb-2 ox-text-muted">Görevleri ne yapalım?</div>

                  <label
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8, cursor: 'pointer', background: deleteMode === 'cascade' ? 'var(--accent-weak)' : 'transparent' }}
                    onClick={() => setDeleteMode('cascade')}
                    data-testid="delete-mode-cascade"
                  >
                    <input
                      type="radio"
                      name="deleteMode"
                      value="cascade"
                      checked={deleteMode === 'cascade'}
                      onChange={() => setDeleteMode('cascade')}
                      style={{ marginTop: 2 }}
                    />
                    <div>
                      <div className="text-[13px] font-medium">Tüm verileri kalıcı olarak sil</div>
                      <div className="text-[11.5px] ox-text-muted" style={{ marginTop: 2 }}>
                        Görevler, dosyalar, yorumlar ve bekleyen onaylar geri alınamaz şekilde silinir.
                      </div>
                    </div>
                  </label>

                  <label
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: 10, border: '1px solid var(--border)', borderRadius: 8, cursor: (deleteImpact.transferTargets || []).length ? 'pointer' : 'not-allowed', opacity: (deleteImpact.transferTargets || []).length ? 1 : 0.5, background: deleteMode === 'transfer' ? 'var(--accent-weak)' : 'transparent' }}
                    onClick={() => { if ((deleteImpact.transferTargets || []).length) setDeleteMode('transfer'); }}
                    data-testid="delete-mode-transfer"
                  >
                    <input
                      type="radio"
                      name="deleteMode"
                      value="transfer"
                      checked={deleteMode === 'transfer'}
                      onChange={() => setDeleteMode('transfer')}
                      disabled={!(deleteImpact.transferTargets || []).length}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div className="text-[13px] font-medium">Görevleri başka projeye taşı</div>
                      <div className="text-[11.5px] ox-text-muted" style={{ marginTop: 2 }}>
                        {(deleteImpact.transferTargets || []).length
                          ? 'Görevler korunur ve seçilecek hedef projeye aktarılır. Sonra bu proje silinir.'
                          : 'Bu workspace\'te taşıma yapılabilecek başka proje yok.'}
                      </div>
                      {deleteMode === 'transfer' && (deleteImpact.transferTargets || []).length > 0 && (
                        <select
                          className="ox-input mt-2"
                          value={transferTarget}
                          onChange={(e) => setTransferTarget(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid="delete-transfer-target"
                          style={{ width: '100%' }}
                        >
                          <option value="">— Hedef proje seçin —</option>
                          {(deleteImpact.transferTargets || []).map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  </label>
                </>
              ) : (
                <p className="text-[12px] ox-text-muted">
                  Bu işlem geri alınamaz.
                </p>
              )}
            </div>
            <div className="ox-modal-foot">
              <button
                className="ox-btn ox-btn-ghost"
                onClick={closeDeleteModal}
                disabled={deleteSubmitting}
              >İptal</button>
              <button
                className="ox-btn ox-btn-danger"
                onClick={handleDelete}
                disabled={
                  deleteSubmitting ||
                  deleteImpactLoading ||
                  (deleteMode === 'transfer' && !transferTarget)
                }
                data-testid="delete-confirm"
              >
                {deleteSubmitting
                  ? 'İşleniyor…'
                  : deleteMode === 'transfer'
                    ? 'Taşı ve Projeyi Sil'
                    : 'Tüm Verileri Sil'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
